// SF3 — what the structure is doing to the account.
//
// The match-type table carries the rule worth repeating: broad is never called
// bad on ACOS alone. Broad exists to discover search terms and is supposed to
// cost more per sale than the exact keywords it finds. Marking it for
// optimisation because it did its job would push a seller to switch off the
// only thing feeding their exact campaigns.
import React from 'react';
import s from '../AdsHealth.module.css';
import { LABEL_TONE, money, ratio } from '../format';

export default function Audit({ data, currency, findings }) {
  if (!data) return <p className={s.empty}>Loading…</p>;

  return (
    <div>
      <h3 className={s.sectionTitle}>Match types</h3>
      <div className={s.tableWrap}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>Match type</th><th>Verdict</th><th className={s.right}>Spend</th>
              <th className={s.right}>Sales</th><th className={s.right}>ACOS</th>
              <th>Why</th>
            </tr>
          </thead>
          <tbody>
            {(data.match_types || []).map((m) => (
              <tr key={m.match_type}>
                <td>{m.match_type}</td>
                <td><span className={s[LABEL_TONE[m.status] || 'muted']}>{m.status}</span></td>
                <td className={s.right}>{money(m.metrics?.spend, currency)}</td>
                <td className={s.right}>{money(m.metrics?.ad_sales, currency)}</td>
                <td className={s.right}>
                  {ratio(m.metrics?.acos, m.metrics?.acos_reason)}
                </td>
                <td className={s.thin} style={{ maxWidth: 420 }}>{m.why}</td>
              </tr>
            ))}
            {!(data.match_types || []).length && (
              <tr><td colSpan={6} className={s.empty}>No targeting rows in this window.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <h3 className={s.sectionTitle}>Structural issues</h3>
      {!(findings || []).length ? (
        <p className={s.empty}>None of the six structural checks fired.</p>
      ) : (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>What</th><th>Where</th><th className={s.right}>Costs</th><th>Why</th>
              </tr>
            </thead>
            <tbody>
              {findings.map((f) => (
                <tr key={f.id}>
                  <td>{f.severity} · {f.type.replace(/_/g, ' ').toLowerCase()}</td>
                  <td>{f.entity}</td>
                  <td className={s.right}>
                    {f.impact != null ? money(f.impact, currency)
                                      : <span className={s.thin}>—</span>}
                  </td>
                  <td className={s.thin} style={{ maxWidth: 480 }}>{f.evidence}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 className={s.sectionTitle}>Targets</h3>
      <div className={s.tableWrap}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>Target</th><th>Match</th><th>Campaign</th><th>Verdict</th>
              <th className={s.right}>Spend</th><th className={s.right}>ACOS</th>
            </tr>
          </thead>
          <tbody>
            {(data.targets || []).map((t) => (
              <tr key={`${t.campaign_id}-${t.ad_group_id}-${t.keyword_id}`}>
                <td>{t.keyword || `Target ${t.keyword_id}`}</td>
                <td className={s.thin}>{(t.match_type || '').toLowerCase()}</td>
                <td className={s.thin}>{t.campaign}</td>
                <td>
                  <span className={s[LABEL_TONE[t.status] || 'muted']} title={t.why}>
                    {t.status}
                  </span>
                </td>
                <td className={s.right}>{money(t.metrics?.spend, currency)}</td>
                <td className={s.right}>{ratio(t.metrics?.acos, t.metrics?.acos_reason)}</td>
              </tr>
            ))}
            {!(data.targets || []).length && (
              <tr><td colSpan={6} className={s.empty}>No targets in this window.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
